import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowUpRight, Loader2, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#0B1027]/70 block mb-2">
        {label}
      </Label>
      {children}
    </div>
  );
}

function DateSelector({ date, setDate }) {
  return (
    <div className="col-span-12 lg:col-span-5 p-6 md:p-10 bg-[#F3F0FF]/40 border-b lg:border-b-0 lg:border-r border-[#0B1027]/10">
      <div className="flex items-center gap-2 mb-5">
        <CalendarDays size={16} className="text-[#0B1027]" />
        <span className="eyebrow">01 · Sélectionnez une date</span>
      </div>
      <div
        data-testid="booking-calendar"
        className="flex justify-center bg-white rounded-2xl p-3 shadow-tactile"
      >
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          locale={fr}
          disabled={(d) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const day = d.getDay();
            return d < today || day === 0 || day === 6;
          }}
        />
      </div>
      <p className="text-xs text-[#0F172A]/60 mt-5 leading-relaxed">
        Disponible du lundi au vendredi · Fuseau Europe/Paris.
      </p>
    </div>
  );
}

function SlotPicker({ date, isoDate, slots, loadingSlots, time, setTime }) {
  if (!isoDate) {
    return (
      <p className="text-sm text-[#0F172A]/60 mb-8 italic">
        Sélectionnez d'abord une date pour voir les créneaux.
      </p>
    );
  }
  return (
    <div className="mb-8">
      <p className="text-sm text-[#0F172A]/80 mb-3">
        Créneaux pour le{" "}
        <span className="font-semibold text-[#0B1027]">
          {format(date, "EEEE d MMMM yyyy", { locale: fr })}
        </span>
      </p>
      {loadingSlots ? (
        <div className="flex items-center gap-2 text-sm text-[#0F172A]/60">
          <Loader2 size={14} className="animate-spin" />
          Chargement des créneaux…
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {slots.map((s) => (
            <button
              type="button"
              key={s.time}
              data-testid={`slot-${s.time}`}
              disabled={!s.available}
              onClick={() => setTime(s.time)}
              className={`h-11 rounded-full text-sm font-medium border transition-all ${
                time === s.time
                  ? "btn-aurora text-white border-transparent shadow-tactile"
                  : s.available
                    ? "bg-white text-[#0B1027] border-[#0B1027]/15 hover:border-[#A855F7] hover:text-[#A855F7]"
                    : "bg-[#F3F0FF] text-[#0F172A]/40 border-transparent cursor-not-allowed line-through"
              }`}
            >
              {s.time}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MinimalForm({ form, updateField, submitting }) {
  return (
    <>
      <div className="eyebrow mb-5">03 · Vos informations</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Prénom *">
          <Input
            required
            data-testid="booking-input-name"
            value={form.name}
            onChange={updateField("name")}
            placeholder="Votre prénom"
            autoComplete="given-name"
            className="rounded-xl h-12 border-[#0B1027]/15 bg-white focus-visible:ring-[#0B1027] focus-visible:ring-offset-0"
          />
        </Field>
        <Field label="Email *">
          <Input
            type="email"
            required
            data-testid="booking-input-email"
            value={form.email}
            onChange={updateField("email")}
            placeholder="vous@exemple.com"
            autoComplete="email"
            className="rounded-xl h-12 border-[#0B1027]/15 bg-white focus-visible:ring-[#0B1027] focus-visible:ring-offset-0"
          />
        </Field>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8 pt-6 border-t border-[#0B1027]/10">
        <div className="text-xs text-[#0F172A]/60 space-y-1 max-w-sm">
          <p className="font-semibold text-[#0B1027]/80">
            En 20 secondes, c'est réservé.
          </p>
          <p>
            Vos données restent privées. Nous en parlons ensemble pendant l'appel.
          </p>
        </div>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit-button"
          className="rounded-full btn-aurora h-14 px-8 text-sm tracking-wide font-semibold shadow-tactile hover-lift"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Réservation…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              Réserver ✓
              <ArrowUpRight size={16} />
            </span>
          )}
        </Button>
      </div>
    </>
  );
}

export default function BookingForm({
  date,
  setDate,
  isoDate,
  slots,
  loadingSlots,
  time,
  setTime,
  form,
  updateField,
  submitting,
  onSubmit,
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="booking-form"
      id="booking-form"
      className="rounded-3xl shadow-tactile-lg overflow-hidden"
      style={{
        background:
          "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%) border-box",
        border: "1px solid transparent",
      }}
    >
      <div className="grid grid-cols-12">
        <DateSelector date={date} setDate={setDate} />

        <div className="col-span-12 lg:col-span-7 p-6 md:p-10">
          <div className="eyebrow mb-5">02 · Choisissez un créneau</div>
          <SlotPicker
            date={date}
            isoDate={isoDate}
            slots={slots}
            loadingSlots={loadingSlots}
            time={time}
            setTime={setTime}
          />
          <MinimalForm
            form={form}
            updateField={updateField}
            submitting={submitting}
          />
        </div>
      </div>
    </form>
  );
}
